import { useEffect } from "react";

/**
 * Lightweight head manager — sets document.title + meta tags + per-page JSON-LD
 * schemas without pulling in react-helmet-async.
 *
 * Props:
 *  - title, description, canonical, ogImage, noIndex (existing)
 *  - schema: a single JSON-LD object OR an array of JSON-LD objects to inject
 *            into <head> as <script type="application/ld+json"> tags.
 *            Each schema is keyed by its @type (or @id) so PageMeta cleans
 *            stale tags on route change.
 */
export default function PageMeta({
  title,
  description,
  canonical,
  ogImage,
  noIndex = false,
  schema = null,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const set = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag || "meta");
        Object.entries(attrs).forEach(([k, v]) => {
          if (k === "tag") return;
          el.setAttribute(k, v);
        });
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => {
          if (k === "tag") return;
          el.setAttribute(k, v);
        });
      }
      return el;
    };

    if (description) {
      set('meta[name="description"]', { name: "description", content: description });
      set('meta[property="og:description"]', { property: "og:description", content: description });
      set('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }
    if (title) {
      set('meta[property="og:title"]', { property: "og:title", content: title });
      set('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    }
    set('meta[property="og:type"]', { property: "og:type", content: "website" });
    set('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    if (ogImage) {
      set('meta[property="og:image"]', { property: "og:image", content: ogImage });
      set('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });
    }
    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
      set('meta[property="og:url"]', {property:"og:url",content:canonical});
    }
    set('meta[name="robots"]', {
      name: "robots",
      content: noIndex ? "noindex,nofollow" : "index,follow",
    });

    /* ----- JSON-LD per-page schemas ----- */
    // Remove any prior page-scoped schema tags (we tag them with data-pagemeta="1")
    const stale = document.head.querySelectorAll('script[type="application/ld+json"][data-pagemeta="1"]');
    stale.forEach((el) => el.remove());

    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s) => {
        if (!s) return;
        const tag = document.createElement("script");
        tag.type = "application/ld+json";
        tag.setAttribute("data-pagemeta", "1");
        tag.textContent = JSON.stringify(s);
        document.head.appendChild(tag);
      });
    }

    return () => {
      // cleanup on unmount: remove only the tags this instance added
      const own = document.head.querySelectorAll('script[type="application/ld+json"][data-pagemeta="1"]');
      own.forEach((el) => el.remove());
    };
  }, [title, description, canonical, ogImage, noIndex, schema]);

  return null;
}
